import { Add, AutoAwesome, CheckCircleRounded, ContentCopy, ControlPointDuplicate, DeleteOutline, Edit, InfoRounded, Upload } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, Chip, Divider, Grid2 as Grid, Stack, styled, TextField, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { DataGridPremiumProps, GridColDef, GridPaginationModel, GridRenderCellParams, GridRenderEditCellParams, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid-premium";
import { QueryStatus } from "@reduxjs/toolkit/dist/query";
import { useSnackbar } from "notistack";
import React from "react";
import { useParams } from "react-router-dom";
import { CustomDataGrid, HighlightSelectBox, MyPaper } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch } from "../../../redux";
import { useAutoPopulateWorkbookItemMutation, useBatchCreateWorkbookMutation, useBulkDeleteWorkbookItemsMutation, useCreateWorkbookItemMutation, useDeleteWorkbookItemMutation, useGetCurrencyQuery, useGetGroupsDDQuery, useGetNounsMappingDDQuery, useGetUoMDropDownQuery, useGetVendorsDDQuery, useImportWorkbookMutation, useUpdateWorkbookItemMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeDialog, openDialog } from "../../../redux/slices/dialog";
import { AddEditRow } from "./addEditRow";
import { SourcingOption } from "../../viewMaterial/sourcing";
import { fields as allFields, typeOptions } from "./utils";
import { chipColor, UserFriendlyView } from "../dashboard/utils";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import { Fields, Result } from "react-spreadsheet-import/types/types";
import { convertArrayToCSV } from "../../../utils";
import { UOMOptions } from "../../viewMaterial/utils";
import { SortOption } from "./addEditWorkBook";

export type QueryType = { data?: any; isFetching?: boolean; isLoading: boolean; status: QueryStatus; isError: boolean; isSuccess: boolean; error?: any };

interface IProps {
    loading: boolean;
    removeChildren: boolean;
    workbookData: any;
    refetch: (id: string) => Promise<void>;
    sparePartCategoryData: QueryType;
    materialStatusData: QueryType;
    bomStatusData: QueryType;
    itemsRes: QueryType;
    refetchItems: () => void;
    paginationData: {
        paginationModel: GridPaginationModel;
        setPaginationModel: (model: GridPaginationModel) => void;
    },
    queryData: {
        queryOptions: SortOption,
        setQueryOptions: React.Dispatch<React.SetStateAction<SortOption>>,
    },
    isViewOnly?: boolean;
}

export interface IRowError {
    _id: string;
    type: boolean;
    group_id: boolean;
    noun_id: boolean;
    uom_id: boolean;
    short_description: boolean;
    reference_id: boolean;
    physical_material?: boolean;
    metric_type: boolean;
    metric_unit?: boolean;
    metric_value?: boolean;
    status: boolean;
    vendor_id?: boolean;
    part_number?: boolean;
    manufacturer_name?: boolean;
    unit_code?: boolean;
    unit_location?: boolean;
    other_details?: boolean;
    cost?: boolean;
    currency?: boolean;
    lead_time_days?: boolean;
    // For Child
    spare_part?: boolean;
    spare_part_category?: boolean;
    spare_part_quantity?: boolean;
    normal_quantity?: boolean;
}

export interface IRow {
    _id: string;
    s_no: number;
    type: SelectBoxOption | null;
    group_id: SelectBoxOption | null;
    noun_id: SelectBoxOption | null;
    uom_id: SelectBoxOption | null;
    short_description: string;
    long_description?: string;
    external_ref_id?: string;
    reference_id?: string;
    physical_material?: SelectBoxOption | null;
    metric_type: SelectBoxOption | null;
    metric_unit?: SelectBoxOption | null;
    metric_value?: number;
    status: SelectBoxOption | null;
    sourcing_type: SelectBoxOption | null;
    vendor_id?: SelectBoxOption | null;
    part_number?: string;
    manufacturer_name?: string;
    unit_code?: string;
    unit_location?: string;
    other_details?: string;
    cost_category?: SelectBoxOption | null;
    cost?: number;
    currency?: SelectBoxOption | null;
    lead_time_days?: number;
    // For Child
    serial_num?: string;
    spare_part?: SelectBoxOption | null;
    spare_part_category?: SelectBoxOption | null;
    spare_part_quantity?: number;
    normal_quantity?: number;
    override_cost?: number;
    child_status?: SelectBoxOption | null;
    validation_errors?: Array<string>;
}

type Field = string | SelectBoxOption | boolean | number | null;

export const SelectBoxRenderer = ({ params, onChange, options, isDisabled = false, label, isLoading = false, hide = false, defaultValue, error = false }: { params: GridRenderEditCellParams; onChange: (value: SelectBoxOption | null) => void, field?: Field; label: string; isDisabled?: boolean; options: SelectBoxOption[]; isLoading?: boolean; skipReset?: boolean; hide?: boolean; defaultValue?: SelectBoxOption | null; error?: boolean; }) => {
    const { value } = params;

    return (
        (<Stack
            sx={{
                width: "100%",
                display: hide ? "none" : 'flex'
            }}>
            <HighlightSelectBox
                label={label}
                margin={"none"}
                disabled={isDisabled}
                loading={isLoading}
                value={options.find(op => (op.value === value?.value || op.label === value?.label) || (op.value === defaultValue?.value || op.label === defaultValue?.label)) ?? null}
                options={options}
                error={error}
                onChange={(option: SelectBoxOption) => {
                    onChange(option)
                }}
            />
        </Stack>)
    );
}

export const InputRenderer = ({ params, onChange, isDisabled = false, placeholder = "Enter value", type = 'text', hide = false, error = false }: { params: GridRenderEditCellParams; onChange: (value: string | number) => void, field?: Field; isDisabled?: boolean; placeholder?: string; type?: React.InputHTMLAttributes<unknown>['type']; defaultValue?: string | number; skipReset?: boolean; hide?: boolean; error?: boolean; }) => {
    const { value } = params;

    return (
        <TextField
            sx={{
                '.MuiOutlinedInput-input': {
                    p: 1
                },
                '.Mui-disabled': { border: error ? "1px solid red !important" : 'inherit' },
                display: hide ? "none" : "block"
            }}
            disabled={isDisabled}
            placeholder={placeholder}
            type={type}
            value={value}
            error={error}
            onChange={(e) => {
                onChange(e.target.value)
            }}
            onKeyDown={(event) => {
                event.stopPropagation();
            }}
        />
    );
}

export const NounOptionsRenderer = ({ params, onChange, isDisabled = false, error = false }: { params: GridRenderEditCellParams; onChange: (value: SelectBoxOption | null) => void; skipReset?: boolean; isDisabled?: boolean; error?: boolean; }) => {
    const { value, row } = params;

    const uom_id = row?.uom_id?.value ?? "";
    const group_id = row?.group_id?.value ?? "";

    const nounResponse = useGetNounsMappingDDQuery({ uom_id, group_id }, { skip: !uom_id || !group_id });

    let nounOptions = uom_id && group_id ? nounResponse?.data ?? [] : [];

    return (
        (<Stack sx={{
            width: "100%"
        }}>
            <HighlightSelectBox
                label={"Select Noun"}
                margin={"none"}
                value={nounOptions.find(op => (op.value === value?.value || op.label === value?.label)) ?? null}
                options={nounOptions}
                loading={nounResponse.isFetching || nounResponse.isLoading}
                disabled={isDisabled}
                error={error}
                onChange={(option: SelectBoxOption) => {
                    onChange(option)
                }}
            />
        </Stack>)
    );
}

export const sparePartOptions = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
];

// Without this style changes are not reflected to table lik removing padding
export const StyledDataGrid = styled(CustomDataGrid)(() => ({}));

export const CellEditable = [
    'short_description',
    'long_description',
    'reference_id',
    'metric_value',
    'cost',
    'lead_time_days',
    'manufacturer_name',
    'part_number',
    'unit_code',
    'unit_location',
    'other_details',
    'notes',
    'serial_num',
    'spare_part_quantity',
    'normal_quantity',
    'override_cost',
    'optional_info'
]

export const ActionTable: React.FC<IProps> = ({ loading, itemsRes, paginationData, removeChildren = false, workbookData, refetch, refetchItems, sparePartCategoryData, materialStatusData, bomStatusData, queryData, isViewOnly }) => {

    const { queryOptions, setQueryOptions } = queryData;

    const dispatch = useAppDispatch();
    const { id = "New" } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const uomData = useGetUoMDropDownQuery({ refetchOnFocus: true });
    const groupData = useGetGroupsDDQuery({ refetchOnFocus: true });
    const supplierData = useGetVendorsDDQuery(null, { refetchOnMountOrArgChange: true, refetchOnFocus: true });
    const currencyData = useGetCurrencyQuery(null);
    const [importCSV] = useImportWorkbookMutation();
    const [batchCreateWorkbook] = useBatchCreateWorkbookMutation();
    const [createWorkbookItem, createRes] = useCreateWorkbookItemMutation();
    const [updateWorkbookItem, updateRes] = useUpdateWorkbookItemMutation();
    const [deleteWorkbookItem, deleteRes] = useDeleteWorkbookItemMutation();
    const [bulkDeleteWorkbookItems, bulkRes] = useBulkDeleteWorkbookItemsMutation();
    const [autoPopulateitem, autoPopulateitemData] = useAutoPopulateWorkbookItemMutation();

    const uomOptions = uomData?.data ?? [];
    const groupOptions = groupData?.data ?? [];
    const supplierOptions = supplierData?.data ?? [];
    const currencyOptions = currencyData?.data ?? [];
    const sparePartCategoryOptions = sparePartCategoryData.data?.spareParts ?? [];
    const childMaterialStatusOptions = materialStatusData?.data?.childStatus ?? [];
    const childBOMStatusOptions = bomStatusData?.data?.childStatus ?? [];

    const [rows, setRows] = React.useState<Partial<IRow>[]>([]);
    const [rowsError, setRowsError] = React.useState<IRowError[]>([]);
    const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [fields, setFields] = React.useState<Fields<any>[]>(allFields);

    const { data: allData = { data: [], totalCount: 0 }, ...rest } = itemsRes;
    const { data, totalCount } = allData;
    const { paginationModel, setPaginationModel } = paginationData;

    const isCompleted = isViewOnly || workbookData?.status === "completed";

    const constructPayload = (currentRow: Partial<IRow>) => {
        return {
            ...currentRow,
            _id: isNaN(Number(currentRow?._id)) ? currentRow?._id : undefined,
            type: currentRow?.type?.value,
            uom_id: currentRow?.uom_id?.value,
            uom_name: currentRow?.uom_id?.label,
            group_id: currentRow?.group_id?.value,
            group_name: currentRow?.group_id?.label,
            noun_id: currentRow?.noun_id?.value,
            noun_name: currentRow?.noun_id?.label,
            metric_type: currentRow?.metric_type?.value,
            metric_unit: currentRow?.metric_unit?.value,
            sourcing_type: currentRow?.sourcing_type?.value,
            vendor_id: currentRow?.vendor_id?.value,
            vendor_name: currentRow?.vendor_id?.label,
            manufacturer_name: currentRow?.manufacturer_name || undefined,
            part_number: currentRow?.part_number || undefined,
            unit_code: currentRow?.unit_code || undefined,
            unit_location: currentRow?.unit_location || undefined,
            other_details: currentRow?.other_details || undefined,
            cost_category: currentRow?.cost_category?.value,
            currency: currentRow?.currency?.value,
            spare_part: [true, "yes"].includes((currentRow?.spare_part?.value || "") as string),
            physical_material: (currentRow?.physical_material === undefined || currentRow?.physical_material === null) ? true : [true, "yes"].includes((currentRow?.physical_material?.value || "") as string),
            metric_value: isNaN(Number(currentRow.metric_value)) ? undefined : Number(currentRow.metric_value) || undefined,
            lead_time_days: isNaN(Number(currentRow.lead_time_days)) ? undefined : Number(currentRow.lead_time_days) || undefined,
            cost: (isNaN(Number(currentRow.cost)) || !currentRow.cost) ? undefined : Number(currentRow.cost),
            normal_quantity: isNaN(Number(currentRow.normal_quantity)) ? 1 : Number(currentRow.normal_quantity),
            spare_part_quantity: isNaN(Number(currentRow.spare_part_quantity)) ? 0 : Number(currentRow.spare_part_quantity),
            long_description: currentRow?.long_description || undefined,
            tags_info: [
                {
                    tag_id: currentRow.spare_part_category?.value,
                    tag_group_id: sparePartCategoryData?.data?.sparePartTagGroup,
                },
                {
                    tag_id: currentRow.child_status?.value,
                    tag_group_id: currentRow?.type?.value === "bom" ? bomStatusData?.data?.statusTagGroup : materialStatusData?.data?.statusTagGroup,
                },
            ].filter(tags => tags?.tag_id),
            validation_errors: undefined
        };
    };

    const updateWorkbookItemAPICall = async (currentRow: Partial<IRow>, payload: any) => {
        return updateWorkbookItem({ id: workbookData?._id, item_id: currentRow?._id, payload: { ...payload, validation_errors: undefined } })
            .unwrap()
            .catch((error) => {
                enqueueSnackbar(error?.data?.title ?? "Unable to update workbook item", { variant: 'error' })
            })

    }

    const onAddOrUpdateRow = (currentRow: Partial<IRow>, isMultiUpdate?: boolean) => {
        const payload = constructPayload(currentRow);
        if (isNaN(Number(currentRow?._id)) && !isMultiUpdate) {
            dispatch(openBackdrop("Updating workbook item..."));
            updateWorkbookItemAPICall(currentRow, payload).then((res: any) => {
                if (res?.message) {
                    enqueueSnackbar(res?.message, { variant: 'success' });
                    refetchItems();
                }
            }).finally(() => {
                dispatch(closeDialog());
                dispatch(closeBackdrop());
            });
        } else if (rowSelectionModel.length && isMultiUpdate) {
            dispatch(openBackdrop(`Updating workbook ${rowSelectionModel.length} items...`));
            Promise.all(rowSelectionModel.map(_id => {
                const row = rows?.find(r => r?._id === _id) ?? {};
                const _payload = constructPayload({ ...row, ...currentRow });
                return updateWorkbookItemAPICall({ _id: `${_id}` }, _payload);
            })).then((res: any[]) => {
                enqueueSnackbar("Workbook Items Updated", { variant: 'success' });
                refetchItems();
            }).finally(() => {
                dispatch(closeDialog());
                dispatch(closeBackdrop());
            });

        } else {
            dispatch(openBackdrop("Creating workbook item..."));
            createWorkbookItem({ id: workbookData?._id, payload: [payload] })
                .unwrap()
                .then((res) => {
                    if (res?.message) {
                        enqueueSnackbar(res?.message, { variant: 'success' });
                        refetchItems();
                    }
                })
                .catch((error) => {
                    enqueueSnackbar(error?.data?.title ?? "Unable to create workbook item", { variant: 'error' })
                })
                .finally(() => {
                    dispatch(closeDialog());
                    dispatch(closeBackdrop());
                });
        }
    };

    const handleEditClick = (_id: string) => {
        dispatch(openDialog({
            title: "Edit Row",
            maxWidth: "lg",
            enablePadding: false,
            body: <AddEditRow _id={_id} rows={rows} rowsError={rowsError} isCompleted={isCompleted} workbookData={workbookData} uomData={uomData} uomOptions={uomOptions} groupData={groupData} groupOptions={groupOptions} supplierData={supplierData} supplierOptions={supplierOptions} currencyData={currencyData} currencyOptions={currencyOptions} sparePartCategoryData={sparePartCategoryData} sparePartCategoryOptions={sparePartCategoryOptions} materialStatusData={materialStatusData} childMaterialStatusOptions={childMaterialStatusOptions} bomStatusData={bomStatusData} childBOMStatusOptions={childBOMStatusOptions} onUpdate={onAddOrUpdateRow} />,
            hidePositiveBtn: true,
            hideNegativeBtn: true
        }));
    };

    const deleteRows = async () => {
        dispatch(openConfirmationDialog({
            title: 'Are you sure?',
            body: `Do you want to delete the workbook items?`,
            positiveBtn: 'Yes',
            negativeBtn: 'No',
            onOk: () => deleteRowsHandler(),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }));
    };

    const bulkEditRows = async () => {
        dispatch(openDialog({
            title: `Edit Rows (${rowSelectionModel.length}) `,
            maxWidth: "lg",
            enablePadding: false,
            body: <AddEditRow _id={``} rows={rows} rowsError={rowsError} isCompleted={isCompleted} workbookData={workbookData} uomData={uomData} uomOptions={uomOptions} groupData={groupData} groupOptions={groupOptions} supplierData={supplierData} supplierOptions={supplierOptions} currencyData={currencyData} currencyOptions={currencyOptions} sparePartCategoryData={sparePartCategoryData} sparePartCategoryOptions={sparePartCategoryOptions} materialStatusData={materialStatusData} childMaterialStatusOptions={childMaterialStatusOptions} bomStatusData={bomStatusData} childBOMStatusOptions={childBOMStatusOptions} onUpdate={onAddOrUpdateRow} isMultiUpdate={true} />,
            hidePositiveBtn: true,
            hideNegativeBtn: true
        }));
    };

    const deleteRowsHandler = () => {
        dispatch(openBackdrop("Deleting Workbook items..."))
        bulkDeleteWorkbookItems({ id: workbookData?._id, item_ids: rowSelectionModel }).then(() => {
            enqueueSnackbar("Workbook items deleted successfully!", { variant: 'success' })
            refetchItems();
        }).catch((error) => {
            console.error(`Error Deleting workbook items: ${error}`);
            enqueueSnackbar(error?.data?.title ?? "Unable to delete workbook items", { variant: 'error' })
        }).finally(() => {
            dispatch(closeBackdrop());
            dispatch(closeConfirmationDialog());
        })
    };

    const handleDelete = async (_id: string) => {
        dispatch(openConfirmationDialog({
            title: 'Are you sure?',
            body: `Do you want to delete the workbook item?`,
            positiveBtn: 'Yes',
            negativeBtn: 'No',
            onOk: () => deleteHandler(_id),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }));
    };

    const deleteHandler = (_id: string) => {
        if (isNaN(Number(_id))) {
            dispatch(openBackdrop("Deleting Workbook item..."));
            deleteWorkbookItem({ id: workbookData?._id, item_id: _id }).then(() => {
                refetchItems();
            }).catch((error) => {
                console.error(`Error Deleting workbook item: ${error}`);
                enqueueSnackbar(error?.data?.title ?? "Unable to delete workbook item", { variant: 'error' })
            }).finally(() => {
                dispatch(closeBackdrop());
                dispatch(closeConfirmationDialog());
            })
        }
    };

    const handleDuplicate = (_id: string) => {
        const row = rows?.find((row: Partial<IRow>) => row?._id === _id);
        if (!row) {
            return;
        }
        const payload = constructPayload(row);
        dispatch(openBackdrop("Duplicating workbook item..."));
        createWorkbookItem({ id: workbookData?._id, payload: { ...payload, short_description: payload?.short_description?.trim() ? payload?.short_description : `Copied item` } })
            .unwrap()
            .then((res) => {
                if (!res?.title) {
                    enqueueSnackbar("Duplicated item successfully!", { variant: 'success' });
                    refetchItems();
                }
            })
            .catch((error) => {
                enqueueSnackbar(error?.data?.title ?? "Unable to duplicate workbook item", { variant: 'error' })
            })
            .finally(() => {
                dispatch(closeDialog());
                dispatch(closeBackdrop());
            });
    };

    let Columns = React.useMemo((): GridColDef[] => {
        let col: GridColDef[] = [
            {
                field: "actions",
                type: "actions",
                headerName: "Actions",
                width: 140,
                cellClassName: "actions",
                getActions: ({ row }: any) => {
                    return [
                        <GridActionsCellItem
                            id="edit-item"
                            icon={<Tooltip title={`${isCompleted ? 'View' : 'Edit'} ${row?.s_no}`}><Edit /></Tooltip>}
                            label="Edit"
                            className="textPrimary"
                            onClick={() => handleEditClick(row?._id)}
                            color="primary"
                        />,
                        <GridActionsCellItem
                            id="duplicate-item"
                            icon={<Tooltip title={`Make a Duplicate of ${row?.s_no}`}><ContentCopy /></Tooltip>}
                            label="Duplicate"
                            className="textPrimary"
                            onClick={() => handleDuplicate(row?._id)}
                            color="inherit"
                            disabled={isCompleted}
                        />,
                        <GridActionsCellItem
                            icon={<Tooltip title={`Delete ${row?.s_no}`}><DeleteOutline /></Tooltip>}
                            label="Delete"
                            className="textPrimary"
                            onClick={() => handleDelete(row?._id)}
                            color="error"
                            disabled={isCompleted}
                        />
                    ];
                },
            },
            {
                field: "validation_errors",
                headerName: "Item Status",
                renderCell: (params: GridRenderCellParams) => {
                    return params?.row?.validation_errors?.length > 0 ? <Tooltip title={params?.row?.validation_errors?.join(", ")}><InfoRounded color="error" /></Tooltip> : <Tooltip title={isCompleted ? `${params?.row?.type?.label ?? "Created"} successfully!` : "No Errors"}><CheckCircleRounded color="success" /></Tooltip>
                },
            },
            // {
            //     field: "s_no",
            //     headerName: "S.No",
            //     width: 100,
            // },
            {
                field: "type",
                width: 250,
                headerName: "Type",
                valueGetter: (value, row, column, apiRef) => row?.type?.value,
                renderCell: ({ row }) => row?.type?.label,
                editable: true
            },
            {
                field: "uom_name",
                width: 250,
                headerName: "Region",
                valueGetter: (value, row, column, apiRef) => row?.uom_id?.label,
                editable: true
            },
            {
                field: "group_name",
                width: 250,
                headerName: "Group",
                valueGetter: (value, row, column, apiRef) => row?.group_id?.label,
                editable: true
            },
            {
                field: "noun_name",
                width: 250,
                headerName: "Noun",
                valueGetter: (value, row, column, apiRef) => row?.noun_id?.label,
                editable: true
            },
            {
                field: "short_description",
                width: 250,
                headerName: "Short Description",
                valueGetter: (value, row, column, apiRef) => row?.short_description,                
                editable: true
            },
            {
                field: "long_description",
                width: 250,
                headerName: "Long Description",
                valueGetter: (value, row, column, apiRef) => row?.long_description,
                editable: true
            },
            {
                field: "reference_id",
                width: 240,
                headerName: "Reference Number",
                valueGetter: (value, row, column, apiRef) => row?.reference_id,
                editable: true
            },
            {
                field: "metric_type",
                width: 220,
                headerName: "UOM",
                valueGetter: (value, row, column, apiRef) => row?.type?.value === "material" ? row?.metric_type?.value : "",
                renderCell: ({ row }) => row?.type?.value === "material" ? row?.metric_type?.label : "",
                editable: true
            },
            {
                field: "metric_value",
                width: 180,
                headerName: "Value",
                valueGetter: (value, row, column, apiRef) => row?.type?.value === "material" ? row?.metric_value : "",
                editable: true
            },
            {
                field: "metric_unit",
                width: 220,
                headerName: "Units",
                valueGetter: (value, row, column, apiRef) => row?.type?.value === "material" ? row?.metric_unit?.value : "",
                renderCell: ({ row }) => row?.type?.value === "material" ? row?.metric_unit?.label : "",
                editable: true
            },
            {
                field: "physical_material",
                width: 180,
                headerName: "Physical Material",
                valueGetter: (value, row, column, apiRef) => row?.type?.value === "material" ? row?.physical_material?.label : "",
                editable: true
            },
            // For Costing Details
            {
                field: "cost_category",
                width: 230,
                headerName: "Costing Type",
                valueGetter: (value, row, column, apiRef) => row?.cost_category?.label,
                editable: true
            },
            {
                field: "cost",
                width: 180,
                headerName: "Cost",
                valueGetter: (value, row, column, apiRef) => row?.cost,
                editable: true
            },
            {
                field: "currency",
                width: 200,
                headerName: "Currency",
                valueGetter: (value, row, column, apiRef) => row?.currency?.label,
                editable: true
            },
            {
                field: "lead_time_days",
                width: 150,
                headerName: "Lead Time (Weeks)",
                valueGetter: (value, row, column, apiRef) => row?.lead_time_days,
                editable: true
            },
            // For Sourcing Details
            {
                field: "sourcing_type",
                width: 250,
                headerName: "Sourcing Type",
                valueGetter: (value, row, column, apiRef) => row?.sourcing_type?.label,
                editable: true
            },
            // For Vendor
            {
                field: "vendor_name",
                width: 250,
                headerName: "Supplier Name",
                valueGetter: (value, row, column, apiRef) => row?.vendor_id?.label,
                editable: true
            },
            {
                field: "manufacturer_name",
                width: 250,
                headerName: "Manufacturer Name",
                valueGetter: (value, row, column, apiRef) => row?.manufacturer_name,
                editable: true
            },
            {
                field: "part_number",
                width: 180,
                headerName: "Part Number",
                valueGetter: (value, row, column, apiRef) => row?.part_number,
                editable: true
            },
            // For Organization
            {
                field: "unit_code",
                width: 180,
                headerName: "Unit Name",
                valueGetter: (value, row, column, apiRef) => row?.unit_code,
                editable: true
            },
            {
                field: "unit_location",
                width: 180,
                headerName: "Unit Location",
                valueGetter: (value, row, column, apiRef) => row?.unit_location,
                editable: true
            },
            {
                field: "other_details",
                width: 180,
                headerName: "Other Details",
                valueGetter: (value, row, column, apiRef) => row?.other_details,
                editable: true
            },
            {
                field: "notes",
                width: 220,
                headerName: "Notes",
                valueGetter: (value, row, column, apiRef) => row?.notes,
                editable: true
            },
            // For Child Fields
            {
                field: "serial_num",
                width: 250,
                headerName: "Tag Number",
                valueGetter: (value, row, column, apiRef) => row?.serial_num,
                editable: true
            },
            {
                field: "spare_part",
                width: 180,
                headerName: "Spare Part",
                valueGetter: (value, row, column, apiRef) => row?.spare_part?.label,
                editable: true
            },
            {
                field: "spare_part_category",
                width: 250,
                headerName: "Spare Part Category",
                valueGetter: (value, row, column, apiRef) => row?.spare_part_category?.label,
                editable: true
            },
            {
                field: "spare_part_quantity",
                width: 125,
                headerName: "Spare Part Qty",
                valueGetter: (value, row, column, apiRef) => row?.spare_part_quantity,
                editable: true
            },
            {
                field: "normal_quantity",
                type: "number",
                headerName: "Qty",
                valueGetter: (value, row, column, apiRef) => row?.normal_quantity,
                editable: true
            },
            {
                field: "override_cost",
                type: "number",
                width: 180,
                headerName: "Override cost per qty",
                valueGetter: (value, row, column, apiRef) => row?.override_cost,
                editable: true
            },
            {
                field: "child_status",
                width: 220,
                headerName: "Child Status",
                valueGetter: (value, row, column, apiRef) => row?.child_status?.label,
                editable: true
            },
            {
                field: "optional_info",
                width: 220,
                headerName: "Remarks",
                valueGetter: (value, row, column, apiRef) => row?.optional_info,
                editable: true
            }
        ];

        if (removeChildren === true) {
            col = col.slice(0, -8)
        }

        return col;
        // eslint-disable-next-line
    }, [removeChildren, rowsError, rows, isCompleted]);

    const addRow = () => {
        let obj: any = {};
        Columns.forEach((col: any) => {
            if (col.field === "normal_quantity") {
                obj[col.field] = 1
            } else {
                obj[col.field] = undefined
            }
        })
        onAddOrUpdateRow({ ...obj, _id: rows?.length });
    };

    const handleCsvUpload = async (file: any) => {
        if (file && id !== "New") {
            dispatch(openBackdrop("Upload workbook..."))
            const formData = new FormData();
            formData.append("file", file);

            await importCSV({ id: id, payload: formData }).unwrap().then((res) => {
                if (res.message) {
                    enqueueSnackbar(
                        res.message ?? "Imported workbook successfully!",
                        { variant: "success" }
                    );
                    refetchItems();
                }
            }).catch((error: any) => {
                enqueueSnackbar(
                    error?.data?.title ?? error?.title ?? "unable to upload the data from the file",
                    { variant: "error" }
                );
                console.error(`Error unable to upload the data from the file: ${error}`);
            }).finally(() => {
                dispatch(closeBackdrop());
            })
        }
        // event.target.value = null;
    };

    const openFileInput = () => {
        setIsOpen(true);
    };

    const handleBatchCreate = async (event: any) => {
        if (hasValidationErrors) {
            enqueueSnackbar(
                "Please fix the errors on the items.",
                { variant: "error" }
            );
            return;
        }
        event?.preventDefault();
        if (id !== "New") {
            dispatch(openBackdrop("Batch creating items from workbook..."))
            await batchCreateWorkbook(id).unwrap().then((res) => {
                if (res.message) {
                    enqueueSnackbar(
                        res.message ?? "Batch created items successfully!",
                        { variant: "success" }
                    );
                    refetch(id);
                    refetchItems();
                }
            }).catch((error: any) => {
                enqueueSnackbar(
                    error.data?.title ?? error?.title ?? "Unable to create items from workbook",
                    { variant: "error" }
                );
                console.error(`Error unable to to create items from workbook: ${error}`);
            }).finally(() => {
                dispatch(closeBackdrop());
            })
        }
    };

    const isLoading = loading || deleteRes.isLoading || rest.isLoading || rest.isFetching || createRes.isLoading || updateRes.isLoading || bulkRes.isLoading;

    const getRowClassName = (params: any) => {
        // Apply the 'errBorderedRow' class if there is an error
        return (Object.values(rowsError.find(r => r?._id === params.row?._id) || {}).some((value) => value === true) || params.row?.validation_errors?.length > 0) ? 'errBorderedRow' : '';
    };

    // const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
    //     if (params.oldIndex > -1 && params.targetIndex > -1) {
    //         setRows((prevRows) => {
    //             const newRows = [...prevRows]; // Copy the array to avoid mutating the original state
    //             const oldItem = newRows[params.oldIndex];
    //             const newItem = newRows[params.targetIndex];

    //             // Swap the entire rows
    //             newRows[params.oldIndex] = newItem;
    //             newRows[params.targetIndex] = oldItem;

    //             // Update serial numbers
    //             newRows.forEach((row, index) => {
    //                 row.s_no = index + 1;
    //             });

    //             return newRows; // Return the updated array
    //         });
    //     }
    // };

    const onSubmit = (data: Result<string>, file: File) => {
        const existingKeys = fields.map(field => field.key);
        data?.all?.forEach((_data: any, index: number) => {
            let Notes = Object.keys(_data).filter(_ => !existingKeys.includes(_)).map(_ => `${_}:${_data[_]}`).join(" - ");

            if (data.validData[index].Notes) {
                data.validData[index].Notes += Notes;
            } else {
                data.validData[index].Notes = Notes;
            }
        });
        handleCsvUpload(convertArrayToCSV(data?.validData ?? [], true));
        setFields([...allFields]);
    };

    const AutoPopulate = () => {

        const [state, setState] = React.useState<{ region: SelectBoxOption | null, group: SelectBoxOption | null }>({ region: null, group: null })

        return (
            <Grid container spacing={2}>
                <Grid size={12}>
                    <HighlightSelectBox
                        id={id}
                        fullWdith
                        label={"Region"}
                        margin={"none"}
                        loading={isLoading}
                        value={state.region}
                        options={uomOptions}
                        onChange={(option: SelectBoxOption) => {
                            setState({ ...state, region: option })
                        }}
                    />
                </Grid>

                <Grid size={12}>
                    <HighlightSelectBox
                        id={id}
                        fullWdith
                        label={"Group"}
                        margin={"none"}
                        loading={isLoading}
                        value={state.group}
                        options={groupOptions}
                        onChange={(option: SelectBoxOption) => {
                            setState({ ...state, group: option })
                        }}
                    />
                </Grid>

                <Grid size={12}>
                    <Alert severity="info">Note: Region, Group and Noun will be auto populated based on the above selected Region and Group by comparing with existing data.</Alert>
                </Grid>

                <Grid size={12}>
                    <LoadingButton fullWidth loading={autoPopulateitemData.isLoading} disabled={state.group || state.region ? false : true} variant="contained" color="primary" onClick={async () => {
                        dispatch(openBackdrop('Auto populating...'))
                        const res: any = await autoPopulateitem({ id, group: state.group, region: state.region })

                        if (res.data) {
                            enqueueSnackbar('Auto populated workbook items', { variant: 'success' })
                            dispatch(closeDialog())
                            refetchItems()
                        } else {
                            enqueueSnackbar('Something went wrong, Unable to Auto Populate', { variant: 'error' })
                        }
                        dispatch(closeBackdrop())
                    }}>Auto Populate</LoadingButton>
                </Grid>

                <Grid size={12}>
                    <Button fullWidth variant="outlined" color="primary" onClick={() => {
                        dispatch(closeDialog())
                    }}>Cancel</Button>
                </Grid>
            </Grid>
        );
    }

    const autoPopulate = () => {
        dispatch(openDialog({
            title: "Auto Populate - Group, Region and Noun",
            body: <AutoPopulate />,
            hideNegativeBtn: true,
            hidePositiveBtn: true
        }))
    }

    const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
        // Here you save the data you need from the sort model
        setQueryOptions({ sortModel: [...sortModel] });
        // eslint-disable-next-line
    }, [])

    const processRowUpdate: NonNullable<DataGridPremiumProps['processRowUpdate']> = (
        newRow,
    ) => {
        onAddOrUpdateRow({
            ...newRow,
            type: newRow?.type?.value ? newRow.type : newRow?.type ? { label: newRow?.type, value: `${newRow?.type}`.toLowerCase() } : typeOptions[0],
            uom_id: { ...newRow.uom_id, label: newRow.uom_name },
            group_id: { ...newRow.group_id, label: newRow.group_name },
            noun_id: { ...newRow.noun_id, label: newRow.noun_name },
            metric_type: newRow.metric_type ? newRow.metric_type.value ? newRow.metric_type : { label: newRow.metric_type, value: newRow.metric_type } : undefined,
            metric_unit: newRow.metric_unit ? newRow.metric_unit.value ? newRow.metric_unit : { label: newRow.metric_unit, value: newRow.metric_unit } : undefined,
            physical_material: newRow.physical_material ? `${newRow.physical_material}`.toLowerCase() === 'yes' ? true : `${newRow.physical_material}`.toLowerCase() === 'no' ? false : undefined : undefined,
            cost_category: newRow.cost_category ? newRow.cost_category.value ? newRow.cost_category : { label: newRow.cost_category, value: newRow.cost_category } : undefined,
            currency: newRow.currency ? newRow.currency.value ? newRow.currency : { label: newRow.currency, value: newRow.currency } : undefined,
            sourcing_type: newRow.sourcing_type ? newRow.sourcing_type.value ? newRow.sourcing_type : { label: newRow.sourcing_type, value: newRow.sourcing_type } : undefined,
            spare_part: newRow.spare_part ? `${newRow.spare_part}`.toLowerCase() === 'yes' ? true : `${newRow.spare_part}`.toLowerCase() === 'no' ? false : undefined : undefined,
        });
        return newRow;
    };

    React.useEffect(() => {
        if (data?.length > 0) {
            const errs = data?.map((d: any) => {
                let obj: Partial<IRowError> = { _id: d?._id };
                if (d.validation_errors) {
                    d.validation_errors?.forEach((e: string) => {
                        if (e.includes("No region found") || e.includes("Error at 'uom_name'")) {
                            obj.uom_id = true;
                        }

                        if (e.includes("No group found") || e.includes("Error at 'group_name'")) {
                            obj.group_id = true;
                        }

                        if (e.includes("No noun found") || e.includes("Error at 'noun_name'")) {
                            obj.noun_id = true;
                        }

                        if (e.includes("Error at 'short_description'") || e.includes("Found duplicate values short/long description")) {
                            obj.short_description = true;
                        }

                        if (e.includes("Error at 'reference_id'")) {
                            obj.reference_id = true;
                        }

                        if (e.includes("Error at 'uom_type'")) {
                            obj.metric_type = true;
                        }

                        if (e.includes("Error at 'currency_code'")) {
                            obj.currency = true;
                        }

                        if (e.includes("vendor cannot be nil")) {
                            obj.vendor_id = true;
                        }

                        if (e.includes("length and length unit") || e.includes("length, weight, length unit and weight unit")) {
                            obj.metric_unit = true;
                            obj.metric_value = true;
                        }

                        if (e.includes("weight and weight unit") || e.includes("length, weight, length unit and weight unit")) {
                            obj.metric_unit = true;
                            obj.metric_value = true;
                        }

                        if (e.includes("value is not one of the allowed values [\"In\",\"Ft\",\"mm\",\"cm\",\"m\"]")) {
                            obj.metric_unit = true;
                            obj.metric_value = true;
                        }

                        if (e.includes("value is not one of the allowed values [\"gram\",\"kg\",\"mt\",\"lb\"]")) {
                            obj.metric_unit = true;
                            obj.metric_value = true;
                        }

                        if (e.includes("value is not one of the allowed values [\"m2\",\"cm2\",\"mm2\",\"ft2\",\"in2\"]")) {
                            obj.metric_unit = true;
                            obj.metric_value = true;
                        }

                        if (e.includes("value is not one of the allowed values [\"m3\",\"mL\",\"L\",\"ft3\",\"ounce\",\"gallon\"]")) {
                            obj.metric_unit = true;
                            obj.metric_value = true;
                        }
                    })
                }
                return obj;
            })
            setRowsError(errs);
        }
        // eslint-disable-next-line
    }, [data])

    React.useEffect(() => {
        if (workbookData?._id) {
            const uD = data?.length > 0 ? data : []
            const updatedRows = uD.map((item: any, idx: number) => {
                let updatedItem = {
                    ...item,
                    bom_type: undefined,
                    s_no: idx + 1,
                    type: (item?.type === typeOptions[0]?.value || item?.type === typeOptions[0]?.label) ? typeOptions[0] : (item?.type === typeOptions[1]?.value || item?.type === typeOptions[1]?.label) ? typeOptions[1] : null,
                    group_id: (item?.group_id || item?.group_name) ? { value: item?.group_id ?? undefined, label: item?.group_name } : null,
                    uom_id: (item?.uom_id || item?.uom_name) ? { value: item?.uom_id ?? undefined, label: item?.uom_name } : null,
                    noun_id: (item?.noun_id || item?.noun_name) ? { value: item?.noun_id ?? undefined, label: item?.noun_name } : null,
                    metric_type: item?.metric_type ? { value: item?.metric_type, label: UOMOptions.find(u => u.value === item?.metric_type)?.label || item?.metric_type } : null,
                    metric_unit: item?.metric_unit ? { value: item?.metric_unit, label: item?.metric_unit } : null,
                    cost_category: item?.cost_category ? { value: item?.cost_category, label: item?.cost_category } : null,
                    currency: item?.currency ? { value: item?.currency, label: item?.currency } : null,
                    sourcing_type: SourcingOption.find(s => (s.value === item?.sourcing_type || s.label === item?.sourcing_type)),
                    vendor_id: (item?.vendor_id || item?.vendor_id) ? { value: item?.vendor_id ?? undefined, label: item?.vendor_name } : null,
                    physical_material: (item?.physical_material || item?.physical_material === undefined) ? sparePartOptions[0] : sparePartOptions[1],
                    spare_part: item?.spare_part ? sparePartOptions[0] : sparePartOptions[1],
                    spare_part_category: sparePartCategoryData?.data?.spareParts?.find((spare: SelectBoxOption) => item?.tags_info?.map((tag: any) => tag?.tag_id).includes(spare?._id)),
                    child_status: (item?.metric_type ? materialStatusData?.data?.childStatus : bomStatusData?.data?.childStatus)?.find((spare: SelectBoxOption) => item?.tags_info?.map((tag: any) => tag?.tag_id).includes(spare?._id)),
                };
                return updatedItem;
            }) ?? [];

            setRows(updatedRows);
        }
        // eslint-disable-next-line
    }, [rest?.status, sparePartCategoryData?.status, materialStatusData?.status, bomStatusData?.status]);

    React.useEffect(() => {
        refetchItems();
        // eslint-disable-next-line
    }, [workbookData?._id, paginationModel, JSON.stringify(queryOptions?.sortModel)]);

    const hasValidationErrors = rows?.some(r => (r?.validation_errors || [])?.length > 0);

    return (
        (<MyPaper padding={0} height={"100%"}>
            <Stack
                sx={{
                    height: "100%",
                    overflow: "hidden"
                }}>
                <Stack
                    direction={"row"}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                    }}>
                    <Stack direction={"row"} spacing={1} sx={{
                        alignItems: "center"
                    }}>
                        <Typography variant="body1" sx={{
                            fontFamily: "htrts_medium"
                        }}>
                            Workbook
                        </Typography>
                        {workbookData?.status &&
                            <Chip size={"small"} label={UserFriendlyView?.[workbookData?.status] ?? ""} color={chipColor?.[workbookData?.status] ?? "primary"} />
                        }
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        {!isCompleted &&
                            <LoadingButton
                                variant="outlined"
                                color="error"
                                onClick={deleteRows}
                                disabled={rowSelectionModel.length <= 0}
                                loading={
                                    loading
                                }
                                startIcon={<DeleteOutline />}
                            >
                                Delete Rows {`(${rowSelectionModel.length})`}
                            </LoadingButton>
                        }

                        {!isCompleted &&
                            <LoadingButton
                                id="edit-workbook-items"
                                variant="outlined"
                                color="info"
                                onClick={bulkEditRows}
                                disabled={rowSelectionModel.length <= 0}
                                loading={
                                    loading
                                }
                                startIcon={<Edit />}
                            >
                                Edit Rows {`(${rowSelectionModel.length})`}
                            </LoadingButton>
                        }

                        <LoadingButton
                            id="add-row"
                            variant="contained"
                            color="success"
                            onClick={addRow}
                            disabled={isCompleted || rowSelectionModel.length > 0}
                            loading={
                                loading
                            }
                            startIcon={<Add />}
                        >
                            Add Row
                        </LoadingButton>

                        <LoadingButton
                            id="load-file"
                            variant="contained"
                            onClick={openFileInput}
                            disabled={isCompleted || rowSelectionModel.length > 0}
                            loading={
                                loading
                            }
                            startIcon={<Upload />}
                        >
                            Load File
                        </LoadingButton>

                        {rows.length > 0 && <LoadingButton
                            variant="contained"
                            onClick={autoPopulate}
                            disabled={isCompleted || rowSelectionModel.length > 0}
                            loading={
                                loading
                            }
                            startIcon={<AutoAwesome />}
                            color="primary"
                        >
                            Auto Populate
                        </LoadingButton>}

                        <Divider orientation="vertical" variant="fullWidth" flexItem />

                        <LoadingButton
                            id="batch-create"
                            variant="contained"
                            onClick={handleBatchCreate}
                            disabled={(data ?? [])?.length <= 0 || isCompleted || rowSelectionModel.length > 0}
                            loading={
                                loading
                            }
                            endIcon={<ControlPointDuplicate />}
                        >
                            Batch Create
                        </LoadingButton>
                    </Stack>


                </Stack>

                {hasValidationErrors && <Alert sx={{ m: 2, mb: 0 }} severity="error">Note: Please fix the errors on the items.</Alert>}

                <Stack
                    sx={{
                        flexGrow: 1,
                        overflow: "auto",
                        p: 2
                    }}>
                    <StyledDataGrid
                        saveLocal={false}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'validation_errors', sort: 'desc' }],
                            },
                        }}
                        columns={Columns}
                        rows={rows}
                        loading={isLoading}
                        getRowId={(row: any) => row?._id ?? row?.s_no}
                        // onCellDoubleClick={(e) => (e.isEditable = false)}
                        getRowClassName={getRowClassName}
                        checkboxSelection={!isCompleted}
                        onRowSelectionModelChange={(newRowSelectionModel) =>
                            setRowSelectionModel(newRowSelectionModel)
                        }
                        rowSelectionModel={rowSelectionModel}
                        cellSelection
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(error) => {
                            console.log('error:', error)
                            enqueueSnackbar('Sorry, Unable to paste the content', { variant: 'error' });
                        }}
                        editMode="cell"
                        isCellEditable={(params) => {                            
                            return CellEditable.includes(params.field);
                        }}
                        sx={{
                            '.MuiDataGrid-virtualScroller': {
                                overflow: 'auto !important',
                                height: "auto !important"
                            },
                        }}
                        showToolbar
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        rowCount={totalCount}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                    />
                </Stack>

                <ReactSpreadsheetImport
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    onSubmit={onSubmit}
                    fields={fields}
                    matchColumnsStepHook={async (data, rawData, columns) => {
                        const customFields = columns
                            .filter((col) => !("value" in col)) // remove columns that were matched
                            .map((col) => {
                                return col
                            })

                        data = rawData.map((_rawData, index) => {
                            let otherNotes = customFields.map(cf => {
                                return `${cf.header}: ${_rawData?.[cf.index] ?? ""}\n`
                            }).join('\n');

                            if (data[index].Notes) {
                                otherNotes = data[index].Notes + '\n\n\n' + otherNotes;
                            }

                            return { ...data[index], Notes: otherNotes };
                        })

                        return data
                    }}
                />
            </Stack>
        </MyPaper>)
    );
}